import React, { useEffect, useState } from 'react';
import { isBlank, isValid, isZipCode, isPhone } from 'util/validate';
import { ArrowLeft32 } from '@carbon/icons-react';
import { updateUserInfo, getUserProfile } from 'redux/user/user-actions';
import { t } from 'components/translation/Translation';
import { getProfile } from 'util/auth';
import { useDispatch } from 'react-redux';
import ProfileForm from './components/profile-form';
import { Buttonv2 as Button } from 'schola-components';
import './edit-profile-form.scss';
import { useResponsiveRender } from 'hooks/useResponsiveRender';
import PropTypes from 'prop-types';
import scrollToError from 'util/scrollToError';

const EditProfileForm = ({ onClose }) => {
  const dispatch = useDispatch();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    parent_first_name: '',
    parent_last_name: '',
    contact: '',
    phone: '',
    zipcode: '',
    current_residence: '',
    language: '',
    how_did_you_hear: '',
    other_how_did_you_hear: '',
  });

  const { renderMobile } = useResponsiveRender({ breakpoint: 565 });

  const tempProfile = getProfile();
  const loadProfile = async () => {
    const {
      name,
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      language,
      address,
      preferred_contact: preferredContact,
      zipcode,
      referred_by: referredBy,
      referred_by_other: referredByOther,
    } = tempProfile;
    let tempForm = {
      parent_first_name: name ? name.split(' ')[0] || '' : firstName || '',
      parent_last_name: name ? name.split(' ')[1] || '' : lastName || '',
      email,
      phone: phone || '',
      contact: preferredContact?.length > 1 ? preferredContact : '',
      zipcode: zipcode?.length > 1 ? zipcode : '',
      current_residence: address?.length > 1 ? address : '',
      language: language?.length > 1 ? language : '',
      how_did_you_hear: referredBy,
      other_how_did_you_hear: referredByOther,
    };

    let userProfile = await dispatch(getUserProfile(tempProfile.sub));
    if (userProfile !== null && userProfile.json) {
      userProfile = JSON.parse(JSON.stringify(userProfile.json));
      tempForm.parent_first_name = userProfile.first_name;
      tempForm.parent_last_name = userProfile.last_name;
      tempForm.phone = userProfile.phone || tempForm.phone;
      tempForm.contact = userProfile.preferred_contact || tempForm.contact;
      tempForm.how_did_you_hear = userProfile.referred_by || tempForm.how_did_you_hear;
      tempForm.other_how_did_you_hear = userProfile.referred_by_other || tempForm.other_how_did_you_hear;
      tempForm.language = userProfile.language || tempForm.language;
      tempForm.current_residence = userProfile.address || tempForm.current_residence;
      tempForm.zipcode = userProfile.zipcode || tempForm.zipcode;
    }
    setForm(tempForm);
    setProfile(tempProfile);
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const [errors, setErrors] = useState({});

  const handleInput = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const validate = (values) => {
    let errors = {};

    if (isBlank(values.parent_first_name)) {
      errors.parent_first_name = t('onboarding:onboarding.parentNameRequiredError');
    } else if (!isValid(values.parent_first_name)) {
      errors.parent_first_name = t('onboarding:onboarding.parentNameValidError');
    }

    if (isBlank(values.parent_last_name)) {
      errors.parent_last_name = t('onboarding:onboarding.parentLastNameRequiredError');
    } else if (!isValid(values.parent_last_name)) {
      errors.parent_last_name = t('onboarding:onboarding.parentNameValidError');
    }

    if (isBlank(values.contact)) {
      errors.contact = t('onboarding:onboarding.requiredField');
    }

    if (isBlank(values.current_residence)) {
      errors.current_residence = t('onboarding:onboarding.requiredField');
    }

    if (isBlank(values.language)) {
      errors.language = t('onboarding:onboarding.requiredField');
    }

    if (isBlank(values.how_did_you_hear)) {
      errors.how_did_you_hear = t('onboarding:onboarding.requiredField');
    } else if (values.how_did_you_hear.toLowerCase() === 'other' && isBlank(values.other_how_did_you_hear)) {
      errors.other_how_did_you_hear = t('onboarding:onboarding.requiredField');
    }

    if (isBlank(values.phone)) {
      errors.phone = t('onboarding:onboarding.requiredField');
    }

    if (!isBlank(values.phone) && !isPhone(values.phone)) {
      errors.phone = t('onboarding:onboarding.phoneIsInvalid');
    }

    if (isBlank(values.zipcode)) {
      errors.zipcode = t('onboarding:onboarding.zipcodeIsRequired');
    } else if (!isZipCode(values.zipcode)) {
      errors.zipcode = t('onboarding:onboarding.zipcodeIsInvalid');
    }

    return errors;
  };

  useEffect(() => {
    scrollToError(errors);
  }, [errors]);

  const handleUpdateUser = async () => {
    let temp = validate(form);
    let err;
    setErrors(temp);
    if (Object.keys(temp).length == 0) {
      // console.log('no errors, can continue')
      let _userInfo = {
        name: `${form.parent_first_name} ${form.parent_last_name}`,
        email: form.email,
      };
      const payloadUser = {
        firstName: form.parent_first_name,
        lastName: form.parent_last_name,
        phone: form.phone.length > 2 ? form.phone : ' ',
        email: form.email || email,
        preferred_contact: form.contact.toLowerCase(),
        language: form.language,
        address: form.current_residence,
        zipcode: form.zipcode,
        referred_by: form.how_did_you_hear,
        referred_by_other: form.other_how_did_you_hear,
      };
      var res = await dispatch(updateUserInfo(profile.sub, payloadUser));

      if (res.type == 'LOGOUT') {
        window.location.reload();
      } else if (res.json.error) {
        err = res?.json?.validationErrors;
      } else {
        // updated
        window.location.reload();
      }
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    handleUpdateUser();
  };

  useEffect(() => {}, [errors]);

  useEffect(() => {
    document.body.classList = 'layoutv3 schola__bodyfixed';

    return () => {
      document.body.className = 'layoutv3';
    };
  }, []);

  return (
    <div className="profile-form-info">
      <div className="profile-form-info__header profile-form-info__edit spacing-py-16 spacing-pr-16">
        {!renderMobile && <h4 className="heading-4">{t('onboarding:onboarding.editProfile')}</h4>}
      </div>

      {renderMobile && (
        <div className="profile-form-info__mobileHeader">
          <span>
            <ArrowLeft32
              className="cursor-pointer"
              onClick={() => {
                onClose();
              }}
            />
          </span>
          <h4 className="heading-4 mobile-title">{t('onboarding:onboarding.editProfile')}</h4>
        </div>
      )}

      <form className="profile-form-info__form" onSubmit={onSubmit}>
        <div className="form-body">
          <div className="scroll-overlay">
            <div className="form-body-scroll spacing-pl-16">
              <p className="font-400 spacing-mt-24 spacing-mb-32 body-regular">{t('onboarding:onboarding.welcome')}</p>
              <ProfileForm form={form} handleInput={handleInput} errors={errors} />
            </div>
          </div>
          <div className="spacing-pr-16 spacing-pl-16">
            <Button type="submit" color="primary" fullWidth>
              {t('onboarding:onboarding.save')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

EditProfileForm.propTypes = {
  onClose: PropTypes.func,
};
export default EditProfileForm;
